import { Component, EventEmitter, OnInit, SecurityContext, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { DomSanitizer } from '@angular/platform-browser';
import { debounceTime, distinctUntilChanged, firstValueFrom, startWith, tap } from 'rxjs';
import { Organization, OrganizationSearchEvent } from 'src/app/features/organization/organization-models';
import { EntityType } from 'src/app/features/prices/action-log';
import OrganizationService from 'src/app/shared/services/organization.service';
import { ActionLogComponent } from '../action-log/action-log.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'adm-action-log-modal',
  templateUrl: './action-log-modal.component.html',
  styleUrls: ['./action-log-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    CustomDropDownArrowDirective,
    ActionLogComponent,
  ],
})
export class ActionLogModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ActionLogModalComponent>>(MatDialogRef);
  data = inject<{
    organizations?: Organization[];
    entityId?: number;
    entityType: EntityType;
    noOrganisationSelector?: boolean;
  }>(MAT_DIALOG_DATA);
  private sanitizer = inject(DomSanitizer);
  private organizationService = inject(OrganizationService);

  public filterModel: FormGroup;
  public organizationFilterEmitter = new EventEmitter<number | null>();
  public typeAheadModel: FormGroup;
  public organizations: Organization[] = [];
  public entityId: number | null = null;
  public entityType: EntityType;
  public filteredOrganizations: Organization[] = [];
  public noOrganisationSelector?: boolean;
  public actionLogReady = false;

  get selectedOrganizationId() {
    return this.filterModel.controls['selectedOrganizationId'];
  }
  get typeAheadQuery() {
    return this.typeAheadModel.controls['typeAheadQuery'];
  }
  async ngOnInit(): Promise<void> {
    this.entityId = this.data.entityId ?? null;
    this.entityType = this.data.entityType;
    this.noOrganisationSelector = this.data.noOrganisationSelector;

    const providedOrganizations = this.data.organizations ?? [];
    this.organizations =
      this.noOrganisationSelector || providedOrganizations.length > 0 ? providedOrganizations : await this.getOrganizations();
    this.filteredOrganizations = [...this.organizations];

    this.filterModel = this.initForm();
    this.typeAheadModel = this.initTypeAhead();

    this.filterModel.valueChanges.subscribe(async val => {
      this.organizationFilterEmitter.next(val.selectedOrganizationId);
    });

    this.actionLogReady = true;
  }

  private initForm() {
    const selectedOrganizationId = null;
    const formGroup = new FormGroup({
      selectedOrganizationId: new FormControl<number | null>(selectedOrganizationId),
    });
    return formGroup;
  }

  private async getOrganizations(): Promise<Organization[]> {
    const pageEvent = new PageEvent();
    pageEvent.pageSize = 10000;

    const searchEvent: OrganizationSearchEvent = {
      paging: pageEvent,
      sorting: null,
    };

    const result = await firstValueFrom(this.organizationService.getOrganizations(searchEvent));
    return result.items;
  }

  public resetOrganization() {
    this.filteredOrganizations = this.organizations;
    this.typeAheadQuery.setValue('');
    this.selectedOrganizationId.setValue(null);
  }
  cancel() {
    this.dialogRef.close();
  }

  public initTypeAhead() {
    let typeAheadQuery = '';
    if (this.selectedOrganizationId.value) {
      typeAheadQuery = this.organizations.find(o => o.id === this.selectedOrganizationId.value)?.name ?? '';
    }
    const typeAheadFormGroup = new FormGroup({
      typeAheadQuery: new FormControl(typeAheadQuery),
    });

    typeAheadFormGroup.valueChanges
      .pipe(
        startWith(''), // let one blank through to search for all
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(() => {
        if (typeof this.typeAheadQuery.value === 'object') {
          const chosenOrganization: Organization = this.typeAheadQuery.value;
          this.selectedOrganizationId.setValue(chosenOrganization.id);
        } else {
          if (this.selectedOrganizationId.value) {
            const selectedOrganization = this.organizations.find(o => o.id === this.selectedOrganizationId.value);
            this.typeAheadQuery.setValue({ id: selectedOrganization?.id, name: selectedOrganization?.name });
          } else if (this.typeAheadQuery.value) {
            this.filteredOrganizations = this.organizations.filter(
              o => o.name.toLowerCase().indexOf(this.typeAheadQuery.value?.toLowerCase()) > -1
            );
          } else {
            this.filteredOrganizations = this.organizations;
          }
        }
      });

    return typeAheadFormGroup;
  }
  public displayFn(organization: Organization | null): string {
    return organization && organization.name ? organization.name : '';
  }
}
